import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  COUNTED_SHIFT_TYPES,
  toIsoDate,
  weekStartOvernightRemainder,
} from "./chkHours";

const DAY_LABELS = ["Пн", "Вт", "Ср", "Чт", "Пт", "Сб", "Вс"];
const HOURS = Array.from({ length: 24 }, (_, hour) => hour);
const HOUR_WIDTH = 32;
const EMPLOYEE_COLUMN_WIDTH = 210;
const ROW_HEIGHT = 58;
const TOTAL_HOURS = 7 * 24;

const COLORS = [
  "#2563eb",
  "#dc2626",
  "#16a34a",
  "#9333ea",
  "#ea580c",
  "#0891b2",
  "#be185d",
  "#ca8a04",
  "#4f46e5",
  "#059669",
  "#c2410c",
  "#7c3aed",
  "#0d9488",
  "#db2777",
  "#65a30d",
];

function formatShortDate(dateString) {
  if (!dateString) return "";
  const [, month, day] = dateString.split("-");
  return `${day}.${month}`;
}

function getEmployeeId(employee) {
  return employee?.id ?? employee?.user_id ?? employee?.employee;
}

function getEmployeeName(employee) {
  const name = `${employee?.surname ?? employee?.user_surname ?? ""} ${
    employee?.name ?? employee?.user_name ?? ""
  }`.trim();
  return name || `Сотрудник ${getEmployeeId(employee)}`;
}

function getShiftRange(shift, weekDates) {
  const start = Number(shift.start);
  const end = Number(shift.end);
  if (Number.isNaN(start) || Number.isNaN(end)) {
    return null;
  }

  const dayIndex = weekDates.findIndex(
    (day) => toIsoDate(day) === toIsoDate(shift.date),
  );

  if (dayIndex < 0) {
    const leftover = weekStartOvernightRemainder(shift, weekDates);
    if (!leftover) return null;
    return { start: leftover.startHour, duration: leftover.duration };
  }

  const absoluteStart = dayIndex * 24 + start;
  let duration;

  if (end === 0) {
    duration = 24 - start;
  } else if (end > start) {
    duration = end - start;
  } else {
    duration = 24 - start + end;
  }

  const absoluteEnd = Math.min(absoluteStart + duration, TOTAL_HOURS);
  if (absoluteEnd <= absoluteStart) return null;

  return {
    start: absoluteStart,
    duration: absoluteEnd - absoluteStart,
  };
}

const ScheduleChkEmployeeWeek = ({
  schedule,
  weekDates = [],
  employees: restaurantEmployees = [],
  selectedTemplate = null,
  canAssign = false,
  hoursByEmployee = {},
  onEmployeeClick,
  onShiftClick,
}) => {
  const shifts = useMemo(
    () =>
      (schedule?.shifts || []).filter((shift) =>
        COUNTED_SHIFT_TYPES.has(Number(shift.type_shift_id)),
      ),
    [schedule?.shifts],
  );

  const employees = useMemo(() => {
    const map = new Map();

    restaurantEmployees.forEach((employee) => {
      const id = getEmployeeId(employee);
      if (!id) return;
      map.set(id, {
        id,
        name: getEmployeeName(employee),
        post: employee.post_name || employee.user_post_name || "",
      });
    });

    shifts.forEach((shift) => {
      const id = shift.employee;
      if (!id) return;
      if (!map.has(id)) {
        map.set(id, {
          id,
          name: getEmployeeName(shift),
          post: shift.user_post_name || "",
        });
      }
    });

    return Array.from(map.values()).sort((a, b) =>
      a.name.localeCompare(b.name, "ru"),
    );
  }, [restaurantEmployees, shifts]);

  const shiftsByEmployee = useMemo(() => {
    const map = {};
    employees.forEach((employee) => {
      map[employee.id] = [];
    });

    shifts.forEach((shift) => {
      const range = getShiftRange(shift, weekDates);
      if (!range || !map[shift.employee]) return;
      map[shift.employee].push({ shift, ...range });
    });

    Object.values(map).forEach((employeeShifts) => {
      employeeShifts.sort((a, b) => a.start - b.start);
    });

    return map;
  }, [employees, shifts, weekDates]);

  const ghostRange = useMemo(() => {
    if (!selectedTemplate) return null;
    return getShiftRange(selectedTemplate, weekDates);
  }, [selectedTemplate, weekDates]);

  const scrollRef = useRef(null);
  const scrollAnimRef = useRef(0);
  const [arrivingDayIndex, setArrivingDayIndex] = useState(-1);

  const selectedDayIndex = selectedTemplate
    ? weekDates.indexOf(selectedTemplate.date)
    : -1;

  useEffect(() => {
    const el = scrollRef.current;
    if (!el || selectedDayIndex < 0) {
      setArrivingDayIndex(-1);
      return undefined;
    }

    const dayWidth = 24 * HOUR_WIDTH;
    const maxScroll = Math.max(0, el.scrollWidth - el.clientWidth);
    const target = Math.max(
      0,
      Math.min(selectedDayIndex * dayWidth, maxScroll),
    );

    if (scrollAnimRef.current) {
      cancelAnimationFrame(scrollAnimRef.current);
    }

    const from = el.scrollLeft;
    const change = target - from;
    setArrivingDayIndex(selectedDayIndex);

    const clearArrive = window.setTimeout(() => {
      setArrivingDayIndex((current) =>
        current === selectedDayIndex ? -1 : current,
      );
    }, 650);

    if (Math.abs(change) < 4) {
      return () => window.clearTimeout(clearArrive);
    }

    const duration = 320;
    const startedAt = performance.now();
    const easeOutCubic = (t) => 1 - (1 - t) ** 3;

    const step = (now) => {
      const progress = Math.min(1, (now - startedAt) / duration);
      el.scrollLeft = from + change * easeOutCubic(progress);
      if (progress < 1) {
        scrollAnimRef.current = requestAnimationFrame(step);
      } else {
        scrollAnimRef.current = 0;
      }
    };
    scrollAnimRef.current = requestAnimationFrame(step);

    return () => {
      window.clearTimeout(clearArrive);
      if (scrollAnimRef.current) {
        cancelAnimationFrame(scrollAnimRef.current);
        scrollAnimRef.current = 0;
      }
    };
  }, [selectedDayIndex, selectedTemplate?.date]);

  if (!employees.length || weekDates.length !== 7) {
    return (
      <div className="schedule-chk-empty">
        Нет сотрудников за выбранную неделю
      </div>
    );
  }

  const timelineWidth = TOTAL_HOURS * HOUR_WIDTH;
  const dayWidth = 24 * HOUR_WIDTH;

  return (
    <div className="chk-week">
      <div className="chk-week-heading">
        <h4 className="chk-week-title">Недельная лента сотрудников</h4>
        <p className="chk-week-hint">
          {canAssign
            ? "Выберите шаблон слева и кликните по сотруднику — смена встанет на его ленту."
            : "Сотрудники расположены строками, смены — слева направо по дням и часам."}
        </p>
      </div>

      <div className="chk-week-scroll" ref={scrollRef}>
        <div
          className="chk-week-grid"
          style={{ width: EMPLOYEE_COLUMN_WIDTH + timelineWidth }}
        >
          <div
            className="chk-week-corner"
            style={{ width: EMPLOYEE_COLUMN_WIDTH }}
          >
            Сотрудник
          </div>

          <div
            className="chk-week-header"
            style={{
              marginLeft: EMPLOYEE_COLUMN_WIDTH,
              width: timelineWidth,
            }}
          >
            <div className="chk-week-day-header">
              {weekDates.map((date, dayIndex) => (
                <div
                  key={date}
                  className={`chk-week-day ${
                    selectedDayIndex === dayIndex ? "is-template-day" : ""
                  } ${arrivingDayIndex === dayIndex ? "is-arriving" : ""}`}
                  style={{ width: dayWidth }}
                >
                  <strong>{DAY_LABELS[dayIndex]}</strong>
                  <span>{formatShortDate(date)}</span>
                </div>
              ))}
            </div>
            <div className="chk-week-hour-header">
              {weekDates.flatMap((date) =>
                HOURS.map((hour) => (
                  <div
                    key={`${date}-${hour}`}
                    className="chk-week-hour"
                    style={{ width: HOUR_WIDTH }}
                  >
                    {String(hour).padStart(2, "0")}
                  </div>
                )),
              )}
            </div>
          </div>

          <div className="chk-week-body">
            {employees.map((employee, employeeIndex) => {
              const employeeShifts = shiftsByEmployee[employee.id] || [];
              const color = COLORS[employeeIndex % COLORS.length];
              const hours =
                hoursByEmployee[employee.id] ||
                hoursByEmployee[Number(employee.id)] ||
                0;
              const hasSelected =
                selectedTemplate &&
                employeeShifts.some(
                  ({ shift }) =>
                    Number(shift.template_shift_id) ===
                    Number(selectedTemplate.id),
                );

              return (
                <div
                  key={employee.id}
                  className={`chk-week-row ${
                    canAssign && selectedTemplate ? "is-assignable" : ""
                  } ${hasSelected ? "has-selected-template" : ""}`}
                  style={{ height: ROW_HEIGHT }}
                  onClick={() => {
                    if (canAssign && selectedTemplate && onEmployeeClick) {
                      onEmployeeClick(employee);
                    }
                  }}
                >
                  <div
                    className="chk-week-employee"
                    style={{ width: EMPLOYEE_COLUMN_WIDTH }}
                  >
                    <span className="chk-week-employee-name">
                      {employee.name}
                    </span>
                    <span className="chk-week-employee-post">
                      {employee.post}
                      {canAssign ? ` · ${hours} ч` : ""}
                    </span>
                  </div>

                  <div
                    className="chk-week-track"
                    style={{
                      marginLeft: EMPLOYEE_COLUMN_WIDTH,
                      width: timelineWidth,
                      height: ROW_HEIGHT,
                      "--hour-width": `${HOUR_WIDTH}px`,
                      "--day-width": `${dayWidth}px`,
                    }}
                  >
                    {ghostRange && (
                      <div
                        key={selectedTemplate.id}
                        className="chk-week-ghost is-arriving"
                        style={{
                          left: ghostRange.start * HOUR_WIDTH + 2,
                          width: Math.max(
                            ghostRange.duration * HOUR_WIDTH - 4,
                            28,
                          ),
                        }}
                      />
                    )}
                    {employeeShifts.map(({ shift, start, duration }) => {
                      const station = shift.station_name || "Без станции";
                      const time = `${shift.start}–${shift.end}`;
                      const isSelectedSlot =
                        selectedTemplate &&
                        Number(shift.template_shift_id) ===
                          Number(selectedTemplate.id);
                      return (
                        <div
                          key={shift.id}
                          className={`chk-week-shift ${
                            isSelectedSlot ? "is-selected-slot" : ""
                          }`}
                          style={{
                            left: start * HOUR_WIDTH + 2,
                            width: Math.max(duration * HOUR_WIDTH - 4, 28),
                            backgroundColor: color,
                          }}
                          title={`${employee.name}\n${shift.date}, ${time}\n${station}`}
                          onClick={(event) => {
                            if (canAssign && selectedTemplate) return;
                            event.stopPropagation();
                            if (onShiftClick) onShiftClick(employee, shift);
                          }}
                        >
                          <span className="chk-week-shift-time">{time}</span>
                          <span className="chk-week-shift-station">
                            {station}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScheduleChkEmployeeWeek;
